import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import type { AnyEvent, AudioState, EventActor } from "../domain/index.js";
import { createEvent, initialState, isMusical, reduceAll } from "../domain/index.js";
import * as api from "../infra/api.js";
import { createEventStream, type ConnectionStatus, type EventStreamHandle } from "../infra/sse.js";

export interface JobUiState {
  id: string;
  kind: string;
  status: string;
  percent?: number;
  error?: string;
  outputHash?: string;
  /**
   * The section this job belongs to, when the job is section-scoped (e.g.
   * `section-variation`). Lets per-section panels show only their own jobs
   * instead of every job of the same `kind`.
   */
  sectionId?: string;
}

export interface MechanicalEventInput {
  type: string;
  projectId: string;
  payload: unknown;
  version?: number;
  createdBy?: EventActor;
}
export interface PlaybackUiState {
  state: "stopped" | "playing" | "paused" | "suspended";
  currentMs: number;
  error?: string;
}
export interface CommandBarUiState {
  status: "idle" | "running" | "error";
  error?: string;
}
export interface UndoUiState {
  status: "idle" | "running" | "error";
  error?: string;
}
export interface GenerationUiState {
  // "requesting" — the POST is in flight; "error" — the POST failed.
  status: "idle" | "requesting" | "error";
  error?: string;
}
export interface VariationsUiState {
  // Per-section A/B status. `requesting` — the variations POST is in flight;
  // `promoting` — a promote POST is in flight; `error` — the last call failed.
  // Keyed by `sectionId` so each section's panel reflects its own state.
  status: Record<string, "idle" | "requesting" | "promoting" | "error">;
  error: Record<string, string>;
}

export interface CompositionStoreState {
  projectId: string | null;
  audioState: AudioState;
  lastGlobalSeq: number;
  lastHash: string;
  connection: ConnectionStatus;
  jobs: Record<string, JobUiState>;
  playback: PlaybackUiState;
  commandBar: CommandBarUiState;
  undoUi: UndoUiState;
  generation: GenerationUiState;
  variations: VariationsUiState;
  cost: { spentUsd: number; capUsd: number; loaded: boolean };
  bootstrap(projectId: string): Promise<void>;
  applyEnvelope(env: AnyEvent): void;
  runCommand(text: string): Promise<void>;
  appendMechanicalEvent(input: MechanicalEventInput): Promise<void>;
  enqueueJob(input: api.CreateJobInput): Promise<string>;
  generateSong(): Promise<string[]>;
  generateVariations(sectionId: string, count: number): Promise<string[]>;
  promoteVariation(sectionId: string, variationId: string): Promise<void>;
  setPlayback(patch: Partial<PlaybackUiState>): void;
  undo(): Promise<void>;
  teardown(): void;
}

interface OperationalEnvelopeShape {
  type: string;
  payload?: {
    jobId?: string;
    kind?: string;
    status?: string;
    percent?: number;
    error?: string;
    assetPath?: string;
    assetHash?: string;
    // `GenerationRequested` carries the section(s) the job targets — used to
    // re-attach a `sectionId` to the job after an SSE replay (refresh).
    sectionIds?: string[];
  };
}

// Sentinel project id used until `bootstrap` overwrites it with the real seed state.
const UNBOOTSTRAPPED = "__unbootstrapped__";

let streamHandle: EventStreamHandle | null = null;

export const useCompositionStore = create<CompositionStoreState>()(
  immer((set, get) => ({
    projectId: null,
    audioState: initialState(UNBOOTSTRAPPED),
    lastGlobalSeq: 0,
    lastHash: "",
    connection: "disconnected",
    jobs: {},
    playback: { state: "stopped", currentMs: 0 },
    commandBar: { status: "idle" },
    undoUi: { status: "idle" },
    generation: { status: "idle" },
    variations: { status: {}, error: {} },
    cost: { spentUsd: 0, capUsd: 0, loaded: false },

    async bootstrap(projectId) {
      // `state` is already materialized through `lastGlobalSeq`; the event stream
      // must resume from that cursor (not 0) so it applies only NEW events. Starting
      // at 0 would re-apply every SectionAdded over the already-materialized state —
      // the reducer appends sections unconditionally, so they would duplicate.
      // `tipHash` is the hash of that same last event: seeding it as `lastHash`
      // lets the first mechanical event the UI appends chain off the log's tip
      // (resuming the stream from the cursor skips the historical-event replay
      // that previously seeded `lastHash` as a side effect).
      const { state, lastGlobalSeq, tipHash } = await api.getProjectState(projectId);
      const seededSeq = lastGlobalSeq ?? 0;
      const seededHash = tipHash ?? "";
      const { spentUsd, capUsd } = await api
        .getCost(projectId)
        .catch(() => ({ spentUsd: 0, capUsd: 0 }));
      set((s) => {
        s.projectId = projectId;
        s.audioState = state;
        s.lastGlobalSeq = seededSeq;
        s.lastHash = seededHash;
        s.connection = "connecting";
        s.jobs = {};
        s.generation = { status: "idle" };
        s.variations = { status: {}, error: {} };
        s.cost = { spentUsd, capUsd, loaded: true };
      });
      if (streamHandle) streamHandle.unsubscribe();
      streamHandle = createEventStream({
        projectId,
        initialLastGlobalSeq: seededSeq,
        onEnvelope: (env) => get().applyEnvelope(env),
        onStatus: (status) =>
          set((s) => {
            s.connection = status;
          }),
      });
    },

    applyEnvelope(env) {
      const seq = (env as { globalSeq?: number }).globalSeq;
      if (typeof seq === "number" && seq <= get().lastGlobalSeq) return;
      const hash = (env as { hash?: string }).hash;
      if (isMusical(env)) {
        const next = reduceAll(get().audioState, [env]);
        set((s) => {
          s.audioState = next;
          if (typeof seq === "number") s.lastGlobalSeq = seq;
          if (typeof hash === "string" && hash.length > 0) s.lastHash = hash;
        });
      } else {
        const op = env as unknown as OperationalEnvelopeShape;
        set((s) => {
          if (typeof seq === "number") s.lastGlobalSeq = seq;
          if (typeof hash === "string" && hash.length > 0) s.lastHash = hash;
          if (op.type === "CostIncurred") {
            const usd = (op.payload as { usd?: number } | undefined)?.usd ?? 0;
            s.cost = { ...s.cost, spentUsd: s.cost.spentUsd + usd };
          }
          const jobId = op.payload?.jobId;
          if (jobId !== undefined) {
            const prev = s.jobs[jobId] ?? {
              id: jobId,
              kind: op.payload?.kind ?? "unknown",
              status: "pending",
            };
            const next: JobUiState = { ...prev };
            if (op.payload?.kind !== undefined) next.kind = op.payload.kind;
            if (op.payload?.status !== undefined) next.status = op.payload.status;
            if (op.payload?.percent !== undefined) next.percent = op.payload.percent;
            if (op.payload?.error !== undefined) next.error = op.payload.error;
            // `GenerationRequested` is the only op-event carrying the target
            // section — capture it so a job restored from an SSE replay is
            // still scoped to its section (survives a refresh).
            const sectionId = op.payload?.sectionIds?.[0];
            if (sectionId !== undefined) next.sectionId = sectionId;
            if (op.type.endsWith("Progress") && op.payload?.percent !== undefined)
              next.status = "running";
            if (op.type.endsWith("Completed")) next.status = "completed";
            if (op.type === "JobFailed") next.status = "failed";
            if (op.type === "ExportCompleted") {
              const outputHash = op.payload?.assetHash ?? op.payload?.assetPath;
              if (outputHash !== undefined) next.outputHash = outputHash;
            }
            // The clip-render job's terminal event is `ClipRendered` (Plan 13):
            // it does not end in `Completed`, so mark the job done explicitly
            // and surface the content-addressed `.mp4` hash for download.
            if (op.type === "ClipRendered") {
              next.status = "completed";
              if (op.payload?.assetHash !== undefined) next.outputHash = op.payload.assetHash;
            }
            s.jobs[jobId] = next;
          }
        });
      }
    },

    async runCommand(text) {
      const projectId = get().projectId;
      if (!projectId) throw new Error("not bootstrapped");
      set((s) => {
        s.commandBar = { status: "running" };
      });
      try {
        const { enqueuedJobs } = await api.runCommand(projectId, text, undefined);
        set((s) => {
          s.commandBar = { status: "idle" };
          // A command can trigger generation (R2.2). Register each enqueued job as
          // `pending` so the Timeline shows the "generating" badge without a
          // refresh — same as `generateSong` / `enqueueJob` do.
          for (const { jobId, kind } of enqueuedJobs) {
            s.jobs[jobId] = { id: jobId, kind, status: "pending" };
          }
        });
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        set((s) => {
          s.commandBar = { status: "error", error };
        });
        throw e;
      }
    },

    async undo() {
      const projectId = get().projectId;
      if (!projectId) throw new Error("not bootstrapped");
      set((s) => {
        s.undoUi = { status: "running" };
      });
      try {
        await api.undo(projectId, undefined);
        set((s) => {
          s.undoUi = { status: "idle" };
        });
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        set((s) => {
          s.undoUi = { status: "error", error };
        });
        throw e;
      }
    },

    async appendMechanicalEvent(input) {
      const envelope = createEvent({
        projectId: input.projectId,
        type: input.type,
        version: input.version ?? 1,
        category: "musical",
        payload: input.payload,
        prevHash: get().lastHash,
        createdBy: input.createdBy ?? "user",
      });
      await api.appendEvent(envelope as unknown as AnyEvent);
      set((s) => {
        s.lastHash = envelope.hash;
      });
    },

    async enqueueJob(input) {
      const { jobId } = await api.createJob(input);
      set((s) => {
        s.jobs[jobId] = { id: jobId, kind: input.kind, status: "pending" };
      });
      return jobId;
    },

    async generateSong() {
      const projectId = get().projectId;
      if (!projectId) throw new Error("not bootstrapped");
      // POST /songs/:id/generate is NOT idempotent — each call enqueues N fresh
      // jobs. Drop the call if one is already in flight to prevent a double-fire.
      if (get().generation.status === "requesting") return [];
      set((s) => {
        s.generation = { status: "requesting" };
      });
      try {
        const { jobIds } = await api.generateSong(projectId);
        set((s) => {
          s.generation = { status: "idle" };
          for (const jobId of jobIds) {
            s.jobs[jobId] = { id: jobId, kind: "generation", status: "pending" };
          }
        });
        return jobIds;
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        set((s) => {
          s.generation = { status: "error", error };
        });
        throw e;
      }
    },

    async generateVariations(sectionId, count) {
      const projectId = get().projectId;
      if (!projectId) throw new Error("not bootstrapped");
      // POST /sections/:id/variations is NOT idempotent — each call enqueues a
      // fresh batch of N jobs. Drop the call if one is already in flight for
      // this section so a double-click cannot fire two batches.
      if (get().variations.status[sectionId] === "requesting") return [];
      set((s) => {
        s.variations.status[sectionId] = "requesting";
        delete s.variations.error[sectionId];
      });
      try {
        const { jobIds } = await api.generateVariations(projectId, sectionId, count);
        set((s) => {
          s.variations.status[sectionId] = "idle";
          // Register each enqueued job as `pending` so the A/B panel shows the
          // "gerando" progress without a refresh — same as `generateSong`.
          // Tag it with `sectionId` so the panel shows only ITS section's jobs.
          for (const jobId of jobIds) {
            s.jobs[jobId] = {
              id: jobId,
              kind: "section-variation",
              status: "pending",
              sectionId,
            };
          }
        });
        return jobIds;
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        set((s) => {
          s.variations.status[sectionId] = "error";
          s.variations.error[sectionId] = error;
        });
        throw e;
      }
    },

    async promoteVariation(sectionId, variationId) {
      const projectId = get().projectId;
      if (!projectId) throw new Error("not bootstrapped");
      set((s) => {
        s.variations.status[sectionId] = "promoting";
        delete s.variations.error[sectionId];
      });
      try {
        // The server appends a `SectionVariationPromoted` musical event; the
        // SSE stream delivers it and the reducer flips `section.audio` +
        // `variation.promoted` — no state mutation here.
        await api.promoteVariation(projectId, sectionId, variationId);
        set((s) => {
          s.variations.status[sectionId] = "idle";
        });
      } catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        set((s) => {
          s.variations.status[sectionId] = "error";
          s.variations.error[sectionId] = error;
        });
        throw e;
      }
    },

    setPlayback(patch) {
      set((s) => {
        s.playback = { ...s.playback, ...patch };
      });
    },

    teardown() {
      if (streamHandle) {
        streamHandle.unsubscribe();
        streamHandle = null;
      }
      set((s) => {
        s.projectId = null;
        s.connection = "disconnected";
        s.jobs = {};
        s.audioState = initialState(UNBOOTSTRAPPED);
        s.lastGlobalSeq = 0;
        s.lastHash = "";
        s.commandBar = { status: "idle" };
        s.undoUi = { status: "idle" };
        s.generation = { status: "idle" };
        s.variations = { status: {}, error: {} };
        s.cost = { spentUsd: 0, capUsd: 0, loaded: false };
      });
    },
  })),
);
